import { AI_CHAT_DRAWER_AREA_RULES, AI_CHAT_DRAWER_PROMPT_CORE } from './ai-chat-drawer.prompt-core';
import type { ToolArea } from './ai-chat-drawer.tool-areas';
import { styleGuideLines } from './ai-chat-drawer.style-guide';

/**
 * The assistant's system prompt, assembled from two files that different people own.
 *
 * `ai-chat-drawer.prompt-core.ts` — the rules whose absence breaks the assistant: the call format,
 * where ids come from, using schemas verbatim, not claiming a call ran. A developer's file.
 *
 * `ai-chat-drawer.style-guide.ts` — what the result should LOOK like. Editable without a developer,
 * and the split is drawn so that emptying it leaves a correct assistant with no visual preferences,
 * rather than a broken one. That property is what makes handing the file over safe; the spec next to
 * these files pins it.
 *
 * The style guide goes LAST, which is also where its one line sat before the split — see the spec's
 * byte-for-byte assertion. Order matters enough to state: a preference that contradicts a core rule
 * should not be the thing the model read most recently.
 */
/**
 * The prompt for one request, carrying only the area rules whose tools are on the table.
 *
 * `areas` is the SAME resolution the tool subset uses, recomputed on the same step — so a rule can
 * never be missing while the tool it describes is present, including after `request_tools` widens
 * the set mid-answer. `null` means "could not tell", exactly as it does for the tools, and yields
 * the whole prompt.
 *
 * The area rules are inserted before the style guide, keeping the property the spec pins: a
 * preference must not be the last thing the model read before a rule it contradicts.
 */
export function buildSystemPrompt(areas: readonly ToolArea[] | null): string {
    const open = areas === null ? null : new Set<ToolArea>(areas);
    const areaRules = AI_CHAT_DRAWER_AREA_RULES.filter(
        (group) => open === null || group.when.some((area) => open.has(area))
    ).flatMap((group) => group.rules);

    return [...AI_CHAT_DRAWER_PROMPT_CORE, ...areaRules, ...styleGuideLines()].join('\n');
}

/** Every rule, for anything that wants the prompt whole — the spec, and a "cannot tell" request. */
export const AI_CHAT_DRAWER_SYSTEM_PROMPT = buildSystemPrompt(null);

/**
 * The one model the chat sends to. Was a picker in the drawer header offering seven ids; the
 * selector is gone, so this is what every request uses.
 *
 * **Opus 5 since 2026-08-22.** It replaced `claude-haiku-4-5`, against which the prompt was
 * originally tuned, because five live runs made the same point five different ways: haiku invented
 * ids (`block_1`), mixed a batch with separate tool calls, wrote `params` for `args`, and needed two
 * or three attempts for a request that should take one. Each of those got a server-side repair, and
 * the repairs kept getting narrower — a sign the model, not the plumbing, was the binding constraint.
 *
 * The id matches the router's prefix-based provider inference (`gpt-`/`claude-`/`gemini-`/
 * `deepseek-`), so `service_id` need not be sent. Anthropic's current line is Opus 5 / Sonnet 5 /
 * Haiku 4.5, and those ids carry no date suffix — appending one 404s.
 *
 * **Two things to watch on this model, in this order.**
 *
 * 1. **Truncated replies.** Opus 5 and Sonnet 5 have adaptive thinking on by default, and
 *    `max_tokens` caps thinking and reply TOGETHER. Nothing here sets it, so the router's default
 *    applies — if replies start cutting off mid-sentence or mid-tool-call, that is the cause, and
 *    the fix is an explicit limit rather than a prompt change.
 * 2. **Whether it is enabled for this account.** A correct id and an enabled id are different
 *    things, and this has never been confirmed for `/v1/proxy/chat/completions` — it is the open
 *    question in §7 of `ai-mcp-plan.md`. A 404 here means "not enabled", not "wrong id".
 *
 * **If it works, expect the prompt to need loosening rather than tightening.** Most of the "never …"
 * rules in the core target fabrications observed on haiku; a stronger model follows instructions far
 * more literally, and rules written to stop a weaker one from inventing things can make a stronger
 * one over-cautious. Re-read them against real runs before adding anything new.
 */
export const AI_CHAT_MODEL = 'claude-opus-5';
